/**
 * @author       david.loehr@zumper.com (David Loehr)
 * @copyright    Copyright (c) 2025, Zumper
 */

import { ApiError } from '@zcode/bizos'
import { nullReducer, payloadReducer } from '@zcode/reducers'
import { combineReducers } from 'redux'
import { handleActions } from 'redux-actions'
import type { ActionTypes, SignIn } from '../../types'
import {
  CLEAR_SIGN_IN,
  CLEAR_SIGN_IN_FIELD_ERROR,
  SET_SIGN_IN_ERROR,
} from './constants'

export const clearFieldErrorReducer = (
  state: ApiError | null,
  { payload: fieldName }: { payload: string }
) => {
  if (state?.failures?.has(fieldName)) {
    // if state looks like
    // { failures: { email: 'Required', last_name: 'Required' }, reason: 'Validation Failed', status: 451}
    // then we just want to remove the provided fieldName, and leave the other failures, like
    // { failures: { email: 'Required' }, reason: 'Validation Failed', status: 451}
    const failures = state.failures.filter(([field]) => field !== fieldName)
    if (!failures.size) {
      return null
    }
    return new ApiError({ ...state._data, failures: failures.toJSON() })
  }
  // if state looks like
  // { field: 'password', reason: 'Must be at least 7 characters', status: 451}
  // (and matches the fieldName we want to remove), then we should remove the whole error
  if (state?.field === fieldName) {
    return null
  }
  return state
}

export const signInReducer = combineReducers<SignIn, ActionTypes>({
  error: handleActions<SignIn['error'], ActionTypes>(
    {
      [CLEAR_SIGN_IN]: nullReducer,
      [CLEAR_SIGN_IN_FIELD_ERROR]: clearFieldErrorReducer,
      [SET_SIGN_IN_ERROR]: payloadReducer,
    },
    null
  ),
})
